<template>
    <header class="page-header">
        <div class="page-header__title">
            <v-tooltip v-if="back" bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small :to="back" v-bind="attrs" v-on="on" aria-label="Volver" class="mr-1">
                        <v-icon small>fas fa-arrow-left</v-icon>
                    </v-btn>
                </template>
                <span>Volver</span>
            </v-tooltip>
            <v-icon v-if="icon" small class="page-header__icon">{{ icon }}</v-icon>
            <h1 class="page-header__text">{{ title }}</h1>
            <span v-if="subtitle" class="page-header__subtitle" :title="subtitle">{{ subtitle }}</span>
            <v-tooltip v-if="help" bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" aria-label="Ayuda" class="ml-1">
                        <v-icon x-small>far fa-question-circle</v-icon>
                    </v-btn>
                </template>
                <span>{{ help }}</span>
            </v-tooltip>
        </div>
        <div class="page-header__actions"><slot name="actions"></slot></div>
        <div class="page-header__primary"><slot name="primary"></slot></div>
    </header>
</template>
<script>
/**
 * Encabezado de pantalla: dónde estás, cómo volver y las acciones.
 *
 * No es un breadcrumb: el menú lateral ya da la jerarquía. Dice en qué
 * módulo o sección estás (`title`), opcionalmente de qué registro
 * (`subtitle`, se corta con … si es largo) y qué hace la pantalla (`help`,
 * un tooltip que abre al pasar el mouse, al enfocar con teclado o al tocar en
 * celular; sin `open-on-click`, que junto al hover lo cerraba al instante).
 * `back` pone una flecha de regreso en las pantallas de detalle.
 *
 * Slots:
 * - `actions`: herramientas (filtros, actualizar, exportar, columnas).
 * - `primary`: la acción principal de la pantalla (un solo botón).
 *
 * En escritorio van en una fila: título, herramientas, acción principal. En
 * celular las herramientas bajan a su propia fila y el título nunca se corta.
 */
export default {
    name: 'PageHeader',
    props: {
        title: { type: String, required: true },
        subtitle: { type: String, default: '' },
        icon: { type: String, default: '' },
        help: { type: String, default: '' },
        // Ruta de regreso; sin ella no hay flecha.
        back: { type: [String, Object], default: null }
    }
}
</script>
<style scoped>
.page-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-height: 48px;
}
.page-header__title {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
}
.page-header__icon {
    margin-right: 8px;
}
.page-header__text {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
}
.page-header__subtitle {
    margin-left: 10px;
    padding-left: 10px;
    border-left: 1px solid var(--app-line-strong);
    font-size: 13px;
    color: var(--app-text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}
.page-header__actions,
.page-header__primary {
    display: flex;
    align-items: center;
    gap: 4px;
}
.page-header__actions:empty,
.page-header__primary:empty {
    display: none;
}
@media (max-width: 599px) {
    .page-header__primary {
        order: 1;
    }
    .page-header__actions {
        order: 2;
        flex-basis: 100%;
    }
}
</style>
